import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    IconButton,
    Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { IContest } from '../../../../types/global/helper';
import { isEmpty } from '../../../../utils/lodash';

interface CreateAwardCategoryDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    contest: IContest;
    editAward?: any;
}

function hasCategories(contest: IContest) {
    return (
        Array.isArray(contest.categories) &&
        contest.categories.length > 0 &&
        contest.categories.some(cat => cat && Object.keys(cat).length > 0 && cat.category_name)
    );
}

const CreateAwardCategoryDialog: React.FC<CreateAwardCategoryDialogProps> = ({
    open,
    onClose,
    onSubmit,
    contest,
    editAward,
}) => {
    const { themeStyle } = useThemeContext();
    const [awardData, setAwardData] = useState({
        category_title: '',
        prize: '',
        additional_prize: [''],
        sub_category: '',
        contest: contest._id,
        category: '',
    });

    const hasEmptyFields =
        isEmpty(awardData.category_title) ||
        !(awardData.prize || awardData.additional_prize) ||
        isEmpty(awardData.category);

    useEffect(() => {
        if (editAward) {
            setAwardData({
                category_title: editAward.category_title,
                prize: editAward.prize?.toString() || '',
                additional_prize: editAward.additional_prize || [''],
                sub_category: editAward.sub_category || '',
                contest: contest._id,
                category: editAward.category || '',
            });
        }
    }, [editAward, contest._id]);

    const handleChange = (field: string, value: string) => {
        setAwardData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleAdditionalPrizeChange = (index: number, value: string) => {
        const newAdditionalPrizes = [...awardData.additional_prize];
        newAdditionalPrizes[index] = value;
        setAwardData(prev => ({
            ...prev,
            additional_prize: newAdditionalPrizes,
        }));
    };

    const addAdditionalPrize = () => {
        setAwardData(prev => ({
            ...prev,
            additional_prize: [...prev.additional_prize, ''],
        }));
    };

    const removeAdditionalPrize = (index: number) => {
        const newAdditionalPrizes = awardData.additional_prize.filter((_, i) => i !== index);
        setAwardData(prev => ({
            ...prev,
            additional_prize: newAdditionalPrizes,
        }));
    };

    const handleSubmit = () => {
        const submitData = {
            ...awardData,
            prize: parseFloat(awardData.prize),
            additional_prize: awardData.additional_prize.filter(prize => prize.trim() !== ''),
        };
        onSubmit(submitData);
    };

    // Add a helper function to format numbers with commas
    const formatNumberWithCommas = (value: string) => {
        const num = value.replace(/,/g, '');
        if (isNaN(Number(num))) return value;
        return Number(num).toLocaleString();
    };

    if (!hasCategories(contest)) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: theme[themeStyle].modal_dark,
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.9)',
                        WebkitTextFillColor: theme[themeStyle].true_white,
                        color: theme[themeStyle].true_white,
                    },
                }}>
                <DialogTitle sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: theme[themeStyle].tag_white }}>
                    Create Award Category
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        Please create submission categories first before creating award categories. Navigate to details,
                        and add at least one submission category
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={onClose}
                        sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: theme[themeStyle].form_black,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                    color: theme[themeStyle].primary_white,
                },
            }}>
            <DialogTitle
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    color: theme[themeStyle].primary_white,
                    backgroundColor: theme[themeStyle].form_black,
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                }}>
                {editAward ? 'Edit Award Category' : 'Create Award Category'}
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: theme[themeStyle].form_black }}>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        label="Award Name"
                        value={awardData.category_title}
                        onChange={e => handleChange('category_title', e.target.value)}
                        fullWidth
                        required
                        sx={{
                            '& .MuiInputLabel-root': {
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: theme[themeStyle].gray_text,
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '& fieldset': { borderColor: theme[themeStyle].primary_white },
                            },
                            '& .MuiInputBase-input': {
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                        }}
                    />
                    <FormControl fullWidth required sx={{ backgroundColor: 'transparent' }}>
                        <InputLabel sx={{ color: theme[themeStyle].primary_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            Category
                        </InputLabel>
                        <Select
                            value={awardData.category}
                            onChange={e => handleChange('category', e.target.value)}
                            label="Category"
                            sx={{
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                background: theme[themeStyle].gray_text,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme[themeStyle].primary_white,
                                },
                                '& .MuiSvgIcon-root': {
                                    color: theme[themeStyle].primary_white,
                                },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: theme[themeStyle].form_black,
                                        color: theme[themeStyle].primary_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                },
                            }}
                            IconComponent={(props: any) => (
                                <ArrowDropDownIcon {...props} sx={{ color: theme[themeStyle].primary_white }} />
                            )}>
                            {contest &&
                                contest.categories &&
                                contest.categories.map((category, index) => (
                                    <MenuItem
                                        key={index}
                                        value={category._id}
                                        sx={{
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            backgroundColor: theme[themeStyle].form_black,
                                            '&:hover': { backgroundColor: theme[themeStyle].highlight },
                                        }}>
                                        {category.category_name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Prize Amount (USD)"
                        type="text"
                        value={formatNumberWithCommas(awardData.prize)}
                        onChange={e => {
                            // Remove non-numeric except dot and comma
                            const raw = e.target.value.replace(/[^\d.,]/g, '').replace(/,/g, '');
                            handleChange('prize', raw);
                        }}
                        onBlur={e => {
                            // Format on blur
                            handleChange('prize', formatNumberWithCommas(e.target.value));
                        }}
                        fullWidth
                        required
                        InputProps={{
                            startAdornment: (
                                <Typography
                                    sx={{ color: theme[themeStyle].primary_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                    $
                                </Typography>
                            ),
                        }}
                        sx={{
                            '& .MuiInputLabel-root': {
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: theme[themeStyle].gray_text,
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '& fieldset': { borderColor: theme[themeStyle].primary_white },
                            },
                            '& .MuiInputBase-input': {
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                        }}
                    />
                    <Typography
                        sx={{
                            color: theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            marginTop: 1,
                            marginBottom: 1,
                        }}>
                        Note: The prize will be shown to users in their local currency.
                    </Typography>
                    <FormControl fullWidth sx={{ backgroundColor: 'transparent' }}>
                        <InputLabel sx={{ color: theme[themeStyle].primary_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            Sub Category
                        </InputLabel>
                        <Select
                            value={awardData.sub_category}
                            onChange={e => handleChange('sub_category', e.target.value)}
                            label="Sub Category"
                            sx={{
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                background: theme[themeStyle].gray_text,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme[themeStyle].primary_white,
                                },
                                '& .MuiSvgIcon-root': {
                                    color: theme[themeStyle].primary_white,
                                },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: theme[themeStyle].form_black,
                                        color: theme[themeStyle].primary_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                },
                            }}
                            IconComponent={(props: any) => (
                                <ArrowDropDownIcon {...props} sx={{ color: theme[themeStyle].primary_white }} />
                            )}>
                            <MenuItem
                                value="male"
                                sx={{
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    backgroundColor: theme[themeStyle].form_black,
                                    '&:hover': { backgroundColor: theme[themeStyle].highlight },
                                }}>
                                Male
                            </MenuItem>
                            <MenuItem
                                value="female"
                                sx={{
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    backgroundColor: theme[themeStyle].form_black,
                                    '&:hover': { backgroundColor: theme[themeStyle].highlight },
                                }}>
                                Female
                            </MenuItem>
                            <MenuItem
                                value="other"
                                sx={{
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    backgroundColor: theme[themeStyle].form_black,
                                    '&:hover': { backgroundColor: theme[themeStyle].highlight },
                                }}>
                                Other
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <Box>
                        <Typography
                            sx={{ color: theme[themeStyle].primary_white, mb: 1, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            Additional Prizes
                        </Typography>
                        {awardData.additional_prize.map((prize, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <TextField
                                    value={prize}
                                    onChange={e => handleAdditionalPrizeChange(index, e.target.value)}
                                    fullWidth
                                    placeholder="Enter additional prize"
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '& fieldset': { borderColor: theme[themeStyle].primary_white },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        },
                                    }}
                                />
                                <IconButton
                                    onClick={() => removeAdditionalPrize(index)}
                                    sx={{
                                        color: theme[themeStyle].primary_white,
                                        '&:hover': { color: theme[themeStyle].highlight },
                                    }}>
                                    <DeleteIcon sx={{ color: theme[themeStyle].primary_white }} />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            startIcon={<AddIcon sx={{ color: theme[themeStyle].primary_white }} />}
                            onClick={addAdditionalPrize}
                            sx={{ color: theme[themeStyle].primary_white, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            Add Prize
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: theme[themeStyle].form_black }}>
                <Button
                    onClick={onClose}
                    sx={{ color: theme[themeStyle].primary_white, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={hasEmptyFields}
                    variant="contained"
                    sx={{
                        backgroundColor: theme[themeStyle].highlight,
                        color: theme[themeStyle].black,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].primary_orange,
                        },
                    }}>
                    {editAward ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateAwardCategoryDialog;
